import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { assert } from '@sama/js-core';
import { isEmpty } from '@sama/micro-dash';
import { combineLatest, merge, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { CuboidTool3D } from '../../../3d/tools/cuboid-tool/cuboid-tool-3d';
import { EditTool3D } from '../../../3d/tools/edit-tool/edit-tool-3d';
import { MeasureTool } from '../../../3d/tools/measure-tool/measure-tool';
import { SelectTool3D } from '../../../3d/tools/select-tool/select-tool-3d';
import { isShapeGroupOrderState3D } from '../../../core/h3ml/state/shape-typing.utils';
import { OrthographicOrchestrator } from '../../../core/outputs/annotation/annotation-shared/fusion/projection/orthographic-orchestrator';
import { answerHelper3D } from '../../../core/outputs/annotation/annotation-shared/shape-layer-answer-helper';
import { MeshLayerState } from '../../../core/outputs/annotation/annotation-shared/state/layers/mesh-layer-state';
import { contentConnectorToken } from '../../../core/outputs/annotation/utils/misc-utils';
import { makeToolProviders } from '../../../cross-team-shared/annotation/shared/abstract-tool';
import { getUrlParam } from '../../../cross-team-shared/misc-utils';
import { getOrThrowStore } from '../../../cross-team-shared/misc-utils/assert-store';
import { isOrthographicVideoOutput } from '../../../cross-team-shared/misc-utils/output-utils';
import { getFrameNumberForEventTracking } from '../../../cross-team-shared/state/sensor-renderer-state';
import {
  LoadTaskResult,
  PointCloudContentConnector,
} from '../../../team-apis/content/content-connector';
import { PointCloudAnswer } from '../../../team-apis/otterdam/answers/point-cloud-answer';
import { PointCloudOutput } from '../../../team-apis/otterdam/outputs/point-cloud-output';
import {
  AbstractAnnotationOutputComponent,
  contentInjectorToken,
} from '../annotation-shared/abstract-annotation-output-component';
import { annotationProviders } from '../annotation-shared/annotation-providers';
import { AbstractAnnotationStore } from '../annotation-shared/state/root/abstract-annotation-store';
import { pointCloudOutputShortcuts } from './point-cloud-output-shortcuts';
import { PointCloudState } from './state/point-cloud-state';
import { PointCloudStore } from './state/point-cloud-store';
import { TransientMeshLayerState } from './state/transient-mesh-layer-state';

@Component({
  selector: 'h3-point-cloud-output',
  templateUrl: './point-cloud-output.component.html',
  providers: [
    annotationProviders,
    makeToolProviders(CuboidTool3D, SelectTool3D, EditTool3D, MeasureTool),
    PointCloudStore,
    { provide: AbstractAnnotationStore, useExisting: PointCloudStore },
    {
      provide: contentConnectorToken,
      deps: [PointCloudOutputComponent],
      useFactory: (self: PointCloudOutputComponent) => self.contentConnector,
    },
    {
      provide: contentInjectorToken,
      deps: [PointCloudOutputComponent],
      useFactory: (self: PointCloudOutputComponent) => self.contentInjector,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PointCloudOutputComponent
  extends AbstractAnnotationOutputComponent<
    PointCloudState,
    PointCloudOutput,
    PointCloudAnswer
  >
  implements OnInit, OnDestroy
{
  showAddRandomShapes$ = combineLatest([
    this.store('status').$,
    this.taskWorkspaceStore('splitViewOutputName').$,
  ]).pipe(
    map(
      ([status, splitViewOutputName]) =>
        (environment.shouldShowAddRandomShapes ||
          getUrlParam('showAddRandomShapes') === 'true') &&
        status === 'ready' &&
        splitViewOutputName === undefined,
    ),
  );
  override contentConnector!: PointCloudContentConnector;

  // These are tested in the keyboard shortcut service to avoid duplicating most
  // of the tests in the spec for this component.
  protected keyboardShortcuts = pointCloudOutputShortcuts;

  private orthographicAnswerChanged$ = new Subject<void>();

  constructor(
    injector: Injector,
    public store: PointCloudStore,
  ) {
    super(store, injector);
    this.initState();

    this.eventTrackingService.addContext({
      frame_number: () => getFrameNumberForEventTracking(store.state()),
    });
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.store.delete();
  }

  protected override cleanUpForNewTask() {
    super.cleanUpForNewTask();
    this.fusionMinion.setOrthographicOrchestrator(
      new OrthographicOrchestrator(
        this.taskWorkspaceStore.getLoadedFramesStore(
          this.annotationOutputName,
        ).$,
        this.orthographicAnswerChanged$,
        this.taskWorkspaceStore,
      ),
    );
    // will be recalculated by content
    this.store('sideViewsIsAllowed').set(false);
  }

  protected answer() {
    const pointCloudShapeGroupOrder =
      this.taskWorkspaceStore.state().outputAnnotationInfo[
        this.annotationOutputName
      ];

    const videoGroups =
      this.taskWorkspaceStore.state().outputAnnotationInfo[
        this.fusionMinion.getOrthographicOrchestrator().getVideoOutputName()
      ];

    const newGroups = this.fusionMinion
      .getOrthographicOrchestrator()
      // @ts-ignore -- fix me
      .getCombinedAnswer(pointCloudShapeGroupOrder, videoGroups);

    assert(
      isShapeGroupOrderState3D(newGroups),
      'annotation should only have 3d shapes',
    );

    const answer = answerHelper3D.shapeGroupOrderExternal(
      newGroups,
      this.sensorRendererStore.state().loadedFrames.length,
    );
    // @ts-ignore -- fix me
    return isEmpty(pointCloudShapeGroupOrder.shapes) ? {} : answer;
  }

  protected resetStoreForNewTask(result: LoadTaskResult) {
    const { width, height } = result;
    const layer = this.createLayerState(width, height);
    const state = new PointCloudState();

    state.layers = [layer];
    state.status = 'ready';
    state.sideViewsIsAllowed = this.store.state().sideViewsIsAllowed;
    this.setSensorRendererState(result);
    this.store.set(state);
    this.resetRightPanelState();
  }

  protected override updateShapeLayer() {
    this.resetOrthographicAnswers();
  }

  protected createLayerState(_width: number, _height: number) {
    this.resetOrthographicAnswers();
    return new MeshLayerState(new TransientMeshLayerState());
  }

  protected override buildUpdateOutputInfoTrigger$() {
    return merge(
      super.buildUpdateOutputInfoTrigger$(),
      this.orthographicAnswerChanged$,
    );
  }

  protected override getShapeGroupOrder() {
    const { project } = this.taskWorkspaceStore.state();
    const orthographicOutput = project.outputs.find(isOrthographicVideoOutput);
    const shapeGroupOrder = super.getShapeGroupOrder();

    if (this.taskWorkspaceStore.isUsingObjects() && orthographicOutput) {
      const orthographicShapeGroupOrder =
        this.objectSortingService.getShapeOrder(orthographicOutput.name);
      return {
        shapes: {
          ...shapeGroupOrder.shapes,
          ...orthographicShapeGroupOrder.shapes,
        },
        groups: {},
        shapeAndGroupOrder: [
          ...shapeGroupOrder.shapeAndGroupOrder,
          ...orthographicShapeGroupOrder.shapeAndGroupOrder,
        ],
      };
    }

    return shapeGroupOrder;
  }

  protected override getShapeGroupOrderChange$() {
    const shapeGroupOrder$ = super.getShapeGroupOrderChange$();
    const { project } = this.taskWorkspaceStore.state();
    const orthographicOutput = project.outputs.find(isOrthographicVideoOutput);

    if (this.taskWorkspaceStore.isUsingObjects() && orthographicOutput) {
      return merge(
        shapeGroupOrder$,
        getOrThrowStore(
          this.taskWorkspaceStore('outputAnnotationInfo'),
          orthographicOutput.name,
        )('shapes').$,
      );
    }

    return shapeGroupOrder$;
  }

  protected override setUpSubscriptions() {
    super.setUpSubscriptions();

    if (!this.taskWorkspaceStore.isUsingObjects()) {
      this.objectlessToolbarKeyboardShortcutService.register3D();
    }

    this.subscribeTo(
      this.taskWorkspaceStore('splitViewOutputName').$,
      (splitViewOutputName) =>
        this.contentConnector.setEditPanelLocation(
          splitViewOutputName ? 'bottom' : 'left',
        ),
    );

    this.subscribeTo(
      this.taskWorkspaceStore('splitViewOutputName').$,
      (splitViewOutputName) =>
        this.contentConnector.setSplitViewOutputName(splitViewOutputName),
    );
  }

  private initState() {
    const state = new PointCloudState();
    state.status = 'loading';

    this.store.set(state);
  }

  private resetOrthographicAnswers() {
    const orthographicOrchestrator =
      this.fusionMinion.getOrthographicOrchestrator();

    orthographicOrchestrator.setInitialAnswer(
      this.outputInfoStore.state().answer,
    );
  }
}
